import type { AIContextSummary, GSCDailyMetrics } from '@ongrid/shared';
import { HistoryChart } from './HistoryChart';
import { StatCard } from './StatCard';
import {
  formatNumber,
  formatPosition,
  getTrendBadgeClass,
  calculateCTR,
} from '../lib/utils';

interface PageDetailProps {
  summary: AIContextSummary;
  history: GSCDailyMetrics[];
  onClose: () => void;
}

export function PageDetail({ summary, history, onClose }: PageDetailProps) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-color)]">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold truncate">{summary.page_url}</h2>
            <p className="text-[var(--text-muted)] text-sm mt-1">
              Last updated: {new Date(summary.last_updated).toLocaleString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Clicks (30d)"
              value={formatNumber(summary.last_30d_clicks)}
            />
            <StatCard
              title="Impressions (30d)"
              value={formatNumber(summary.last_30d_impressions)}
            />
            <StatCard
              title="CTR"
              value={calculateCTR(summary.last_30d_clicks, summary.last_30d_impressions)}
            />
            <StatCard
              title="Avg Position"
              value={formatPosition(summary.avg_position)}
            />
          </div>

          {/* Trend and metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-[var(--bg-tertiary)] rounded-lg p-4">
              <p className="text-[var(--text-muted)] text-sm mb-1">Trend Status</p>
              <span className={getTrendBadgeClass(summary.trend_status)}>
                {summary.trend_status ?? 'Unknown'}
              </span>
            </div>
            <div className="bg-[var(--bg-tertiary)] rounded-lg p-4">
              <p className="text-[var(--text-muted)] text-sm mb-1">Page Age</p>
              <p className="text-lg font-medium">
                {summary.page_age_days !== null ? `${summary.page_age_days} days` : '—'}
              </p>
            </div>
            <div className="bg-[var(--bg-tertiary)] rounded-lg p-4">
              <p className="text-[var(--text-muted)] text-sm mb-1">Days to First Click</p>
              <p className="text-lg font-medium">
                {summary.days_to_first_click !== null
                  ? `${summary.days_to_first_click} days`
                  : '—'}
              </p>
            </div>
            <div className="bg-[var(--bg-tertiary)] rounded-lg p-4">
              <p className="text-[var(--text-muted)] text-sm mb-1">First Indexed</p>
              <p className="text-lg font-medium">
                {summary.first_indexed_date ?? '—'}
              </p>
            </div>
          </div>

          {/* History chart */}
          <div className="bg-[var(--bg-tertiary)] rounded-lg p-4">
            <h3 className="text-lg font-medium mb-4">Performance History</h3>
            <HistoryChart data={history} />
          </div>
        </div>
      </div>
    </div>
  );
}

